import type {
  SalesAssistantSavedViewWriteValues,
  SalesCockpitRecommendation,
  SalesCockpitViewMode,
  SalesContactOutcome,
  SalesTaskActivityType,
  SalesTaskSummary,
} from '@erp/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCustomerContacts } from '@/features/customers';
import {
  useBulkAssignSalesRecommendations,
  useBulkSnoozeSalesRecommendations,
  useCreateSalesTask,
  useRecordSalesTaskOutcome,
  useSalesAssignees,
  useSalesTeams,
  useSaveSalesAssistantView,
  useSnoozeSalesRecommendation,
} from '../api/use-sales-assistant';

function localInput(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function toIso(value: string): string {
  return new Date(value).toISOString();
}

function taskActivityFor(recommendation: SalesCockpitRecommendation): SalesTaskActivityType {
  if (recommendation.suggestedAction === 'CALL') return 'CALL';
  if (recommendation.suggestedAction === 'PLAN_CALLBACK') return 'CALLBACK';
  if (recommendation.suggestedAction === 'CREATE_QUOTE') return 'QUOTE';
  if (recommendation.suggestedAction === 'RESOLVE_COMPLAINT') return 'COMPLAINT';
  return 'FOLLOW_UP';
}

export function CreateTaskDialog({
  recommendation,
  onOpenChange,
}: {
  recommendation: SalesCockpitRecommendation | null;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const createTask = useCreateSalesTask();
  const [title, setTitle] = useState('');
  const [reason, setReason] = useState('');
  const [dueAt, setDueAt] = useState(localInput(new Date(Date.now() + 86_400_000)));
  const [activityType, setActivityType] = useState<SalesTaskActivityType>('FOLLOW_UP');
  useEffect(() => {
    if (recommendation === null) return;
    setTitle(t(`salesAssistant.cockpit.ruleTypes.${recommendation.type}`));
    setReason(t(`salesAssistant.cockpit.ruleTypes.${recommendation.type}`));
    setDueAt(localInput(recommendation.dueAt));
    setActivityType(taskActivityFor(recommendation));
  }, [recommendation, t]);
  const valid =
    title.trim() !== '' && reason.trim() !== '' && Number.isFinite(new Date(dueAt).getTime());
  return (
    <Dialog open={recommendation !== null} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('salesAssistant.cockpit.taskDialog.title')}</DialogTitle>
          <DialogDescription>{recommendation?.customerName}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="sales-task-title">{t('common.title')}</Label>
            <Input
              id="sales-task-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sales-task-reason">
              {t('salesAssistant.cockpit.taskDialog.reason')}
            </Label>
            <Textarea
              id="sales-task-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="sales-task-due">{t('salesAssistant.cockpit.taskDialog.dueAt')}</Label>
              <Input
                id="sales-task-due"
                type="datetime-local"
                value={dueAt}
                onChange={(event) => setDueAt(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('salesAssistant.cockpit.taskDialog.activityType')}</Label>
              <Select
                value={activityType}
                onValueChange={(value) => setActivityType(value as SalesTaskActivityType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(
                    [
                      'CALL',
                      'CALLBACK',
                      'VISIT',
                      'MEETING',
                      'EMAIL',
                      'QUOTE',
                      'FOLLOW_UP',
                      'COMPLAINT',
                      'OTHER',
                    ] as const
                  ).map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`salesAssistant.cockpit.taskActivities.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            disabled={!valid || createTask.isPending || recommendation === null}
            onClick={() => {
              if (recommendation === null || !valid) return;
              createTask.mutate(
                {
                  customerId: recommendation.customerId,
                  recommendationId: recommendation.id,
                  title: title.trim(),
                  reason: reason.trim(),
                  assignedUserId: recommendation.assignedUserId,
                  assignedTeamId: recommendation.assignedTeamId,
                  priority: recommendation.priority,
                  dueAt: toIso(dueAt),
                  activityType,
                  checklist: [],
                  attachmentIds: [],
                },
                {
                  onSuccess: () => {
                    toast.success(t('salesAssistant.cockpit.taskDialog.success'));
                    onOpenChange(false);
                  },
                  onError: (error) => toast.error(error.message),
                },
              );
            }}
          >
            {createTask.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {t('salesAssistant.cockpit.taskDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SnoozeDialog({
  recommendationIds,
  onOpenChange,
  onSuccess,
}: {
  recommendationIds: string[];
  onOpenChange: (open: boolean) => void;
  onSuccess?: (() => void) | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const single = useSnoozeSalesRecommendation();
  const bulk = useBulkSnoozeSalesRecommendations();
  const [until, setUntil] = useState(localInput(new Date(Date.now() + 7 * 86_400_000)));
  const [reason, setReason] = useState(t('salesAssistant.cockpit.snoozeDialog.defaultReason'));
  const pending = single.isPending || bulk.isPending;
  return (
    <Dialog open={recommendationIds.length > 0} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('salesAssistant.cockpit.snoozeDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('salesAssistant.cockpit.selected', { count: recommendationIds.length })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="sales-snooze-until">
              {t('salesAssistant.cockpit.snoozeDialog.until')}
            </Label>
            <Input
              id="sales-snooze-until"
              type="datetime-local"
              value={until}
              onChange={(event) => setUntil(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sales-snooze-reason">
              {t('salesAssistant.cockpit.snoozeDialog.reason')}
            </Label>
            <Textarea
              id="sales-snooze-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            disabled={pending || reason.trim() === '' || new Date(until) <= new Date()}
            onClick={() => {
              const input = { snoozedUntil: toIso(until), reason: reason.trim() };
              if (recommendationIds.length === 1) {
                single.mutate(
                  { recommendationId: recommendationIds[0] ?? '', ...input },
                  {
                    onSuccess: () => {
                      toast.success(t('salesAssistant.cockpit.bulkSnoozed', { count: 1 }));
                      onOpenChange(false);
                      onSuccess?.();
                    },
                    onError: (error) => toast.error(error.message),
                  },
                );
              } else {
                bulk.mutate(
                  { recommendationIds, ...input },
                  {
                    onSuccess: (result) => {
                      toast.success(
                        t('salesAssistant.cockpit.bulkSnoozed', { count: result.updated }),
                      );
                      onOpenChange(false);
                      onSuccess?.();
                    },
                    onError: (error) => toast.error(error.message),
                  },
                );
              }
            }}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {t('salesAssistant.cockpit.snoozeDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AssignDialog({
  recommendationIds,
  onOpenChange,
  onSuccess,
}: {
  recommendationIds: string[];
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const assignees = useSalesAssignees();
  const teams = useSalesTeams();
  const assign = useBulkAssignSalesRecommendations();
  const [userId, setUserId] = useState('NONE');
  const [teamId, setTeamId] = useState('NONE');
  return (
    <Dialog open={recommendationIds.length > 0} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('salesAssistant.cockpit.assignDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('salesAssistant.cockpit.selected', { count: recommendationIds.length })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t('salesAssistant.cockpit.assignDialog.user')}</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger>
                <SelectValue placeholder={t('salesAssistant.cockpit.assignDialog.chooseUser')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">
                  {t('salesAssistant.cockpit.fields.unassigned')}
                </SelectItem>
                {(assignees.data?.data ?? []).map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('salesAssistant.cockpit.assignDialog.team')}</Label>
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger>
                <SelectValue placeholder={t('salesAssistant.cockpit.assignDialog.chooseTeam')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">
                  {t('salesAssistant.cockpit.fields.unassigned')}
                </SelectItem>
                {(teams.data?.data ?? []).map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            disabled={assign.isPending || (userId === 'NONE' && teamId === 'NONE')}
            onClick={() =>
              assign.mutate(
                {
                  recommendationIds,
                  assignedUserId: userId === 'NONE' ? null : userId,
                  assignedTeamId: teamId === 'NONE' ? null : teamId,
                },
                {
                  onSuccess: (result) => {
                    toast.success(
                      t('salesAssistant.cockpit.bulkAssigned', { count: result.updated }),
                    );
                    onOpenChange(false);
                    onSuccess();
                  },
                  onError: (error) => toast.error(error.message),
                },
              )
            }
          >
            {assign.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {t('salesAssistant.cockpit.assignDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SaveViewDialog({
  open,
  viewMode,
  filters,
  onOpenChange,
}: {
  open: boolean;
  viewMode: SalesCockpitViewMode;
  filters: SalesAssistantSavedViewWriteValues['filters'];
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const save = useSaveSalesAssistantView();
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('salesAssistant.cockpit.actions.saveView')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="saved-view-name">{t('salesAssistant.cockpit.viewName')}</Label>
            <Input
              id="saved-view-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <label className="flex items-center gap-3 text-sm">
            <Checkbox
              checked={isDefault}
              onCheckedChange={(value) => setIsDefault(value === true)}
            />
            {t('salesAssistant.cockpit.defaultView')}
          </label>
        </div>
        <DialogFooter>
          <Button
            type="button"
            disabled={save.isPending || name.trim() === ''}
            onClick={() =>
              save.mutate(
                { input: { name: name.trim(), viewMode, filters, isDefault } },
                {
                  onSuccess: () => {
                    toast.success(t('salesAssistant.cockpit.viewSaved'));
                    setName('');
                    onOpenChange(false);
                  },
                  onError: (error) => toast.error(error.message),
                },
              )
            }
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {t('salesAssistant.cockpit.actions.saveView')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const OUTCOMES: readonly SalesContactOutcome[] = [
  'REACHED',
  'NOT_REACHED',
  'CALLBACK_REQUESTED',
  'CALLBACK_SCHEDULED',
  'ORDER_EXPECTED',
  'QUOTE_REQUESTED',
  'SAMPLE_REQUESTED',
  'NO_CURRENT_NEED',
  'CONTACT_LATER',
  'CONTACT_CHANGED',
  'TEMPORARILY_CLOSED',
  'CUSTOMER_LOST',
  'INVALID_CONTACT_DATA',
  'COMPLAINT_OR_PROBLEM',
  'OTHER',
];

export function TaskOutcomeDialog({
  task,
  onOpenChange,
  initialOutcome = 'REACHED',
  direction,
  occurredAt,
  idempotencyKey,
  onSuccess,
}: {
  task: SalesTaskSummary | null;
  onOpenChange: (open: boolean) => void;
  initialOutcome?: SalesContactOutcome | undefined;
  direction?: 'INBOUND' | 'OUTBOUND' | null | undefined;
  occurredAt?: string | undefined;
  idempotencyKey?: string | undefined;
  onSuccess?: (() => void) | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const record = useRecordSalesTaskOutcome();
  const contacts = useCustomerContacts(task?.customerId ?? '');
  const [outcome, setOutcome] = useState<SalesContactOutcome>(initialOutcome);
  const [note, setNote] = useState('');
  const [nextContactAt, setNextContactAt] = useState(localInput(new Date(Date.now() + 86_400_000)));
  const [expectedOrderDate, setExpectedOrderDate] = useState('');
  const [expectedRevenue, setExpectedRevenue] = useState('');
  const [newContactId, setNewContactId] = useState('');
  const [reason, setReason] = useState('');
  const [closedUntil, setClosedUntil] = useState('');
  const [complaintSubject, setComplaintSubject] = useState('');
  const [severity, setSeverity] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>('MEDIUM');
  const [createQuote, setCreateQuote] = useState(true);
  useEffect(() => {
    if (task === null) return;
    setOutcome(initialOutcome);
    setNote('');
  }, [initialOutcome, task]);
  const needsNext = ['CALLBACK_REQUESTED', 'CALLBACK_SCHEDULED', 'CONTACT_LATER'].includes(outcome);
  const valid = useMemo(() => {
    if (needsNext && new Date(nextContactAt) <= new Date()) return false;
    if (outcome === 'ORDER_EXPECTED' && expectedOrderDate === '') return false;
    if (outcome === 'CONTACT_CHANGED' && newContactId === '') return false;
    if (['NO_CURRENT_NEED', 'CUSTOMER_LOST'].includes(outcome) && reason.trim() === '')
      return false;
    if (outcome === 'TEMPORARILY_CLOSED' && closedUntil === '') return false;
    if (outcome === 'COMPLAINT_OR_PROBLEM' && complaintSubject.trim() === '') return false;
    return outcome !== 'OTHER' || note.trim() !== '';
  }, [
    closedUntil,
    complaintSubject,
    expectedOrderDate,
    needsNext,
    newContactId,
    nextContactAt,
    note,
    outcome,
    reason,
  ]);
  return (
    <Dialog open={task !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('salesAssistant.cockpit.outcomeDialog.title')}</DialogTitle>
          <DialogDescription>{task?.customerName}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('salesAssistant.cockpit.outcomeDialog.outcome')}</Label>
            <Select
              value={outcome}
              onValueChange={(value) => setOutcome(value as SalesContactOutcome)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OUTCOMES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`salesAssistant.outcomes.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="outcome-note">{t('salesAssistant.cockpit.outcomeDialog.note')}</Label>
            <Textarea
              id="outcome-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          {needsNext ? (
            <div className="space-y-1.5">
              <Label htmlFor="outcome-next">
                {t('salesAssistant.cockpit.outcomeDialog.nextContact')}
              </Label>
              <Input
                id="outcome-next"
                type="datetime-local"
                value={nextContactAt}
                onChange={(event) => setNextContactAt(event.target.value)}
              />
            </div>
          ) : null}
          {outcome === 'ORDER_EXPECTED' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="outcome-order-date">
                  {t('salesAssistant.cockpit.outcomeDialog.expectedOrder')}
                </Label>
                <Input
                  id="outcome-order-date"
                  type="date"
                  value={expectedOrderDate}
                  onChange={(event) => setExpectedOrderDate(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="outcome-revenue">
                  {t('salesAssistant.cockpit.outcomeDialog.expectedRevenue')}
                </Label>
                <Input
                  id="outcome-revenue"
                  inputMode="decimal"
                  value={expectedRevenue}
                  onChange={(event) => setExpectedRevenue(event.target.value)}
                />
              </div>
            </div>
          ) : null}
          {outcome === 'CONTACT_CHANGED' ? (
            <div className="space-y-1.5">
              <Label>{t('salesAssistant.outcomes.CONTACT_CHANGED')}</Label>
              <Select value={newContactId} onValueChange={setNewContactId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(contacts.data ?? []).map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.firstName} {contact.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {outcome === 'NO_CURRENT_NEED' || outcome === 'CUSTOMER_LOST' ? (
            <div className="space-y-1.5">
              <Label htmlFor="outcome-reason">
                {t(
                  outcome === 'CUSTOMER_LOST'
                    ? 'salesAssistant.cockpit.outcomeDialog.lossReason'
                    : 'salesAssistant.cockpit.outcomeDialog.noNeedReason',
                )}
              </Label>
              <Textarea
                id="outcome-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          ) : null}
          {outcome === 'TEMPORARILY_CLOSED' ? (
            <div className="space-y-1.5">
              <Label htmlFor="outcome-closed">
                {t('salesAssistant.cockpit.outcomeDialog.closedUntil')}
              </Label>
              <Input
                id="outcome-closed"
                type="date"
                value={closedUntil}
                onChange={(event) => setClosedUntil(event.target.value)}
              />
            </div>
          ) : null}
          {outcome === 'QUOTE_REQUESTED' ? (
            <label className="flex items-center gap-3 text-sm">
              <Checkbox
                checked={createQuote}
                onCheckedChange={(value) => setCreateQuote(value === true)}
              />
              {t('salesAssistant.cockpit.outcomeDialog.createQuote')}
            </label>
          ) : null}
          {outcome === 'COMPLAINT_OR_PROBLEM' ? (
            <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
              <div className="space-y-1.5">
                <Label htmlFor="outcome-complaint">
                  {t('salesAssistant.cockpit.outcomeDialog.complaintSubject')}
                </Label>
                <Input
                  id="outcome-complaint"
                  value={complaintSubject}
                  onChange={(event) => setComplaintSubject(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t('salesAssistant.cockpit.outcomeDialog.severity')}</Label>
                <Select
                  value={severity}
                  onValueChange={(value) => setSeverity(value as typeof severity)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const).map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`salesAssistant.cockpit.priorities.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            type="button"
            disabled={!valid || record.isPending || task === null}
            onClick={() => {
              if (task === null || !valid) return;
              record.mutate(
                {
                  taskId: task.id,
                  input: {
                    idempotencyKey: idempotencyKey ?? `cockpit-outcome-${task.id}`,
                    outcome,
                    occurredAt: occurredAt ?? new Date().toISOString(),
                    direction:
                      direction ??
                      (['CALL', 'CALLBACK'].includes(task.activityType) ? 'OUTBOUND' : null),
                    note: note.trim() === '' ? null : note.trim(),
                    nextContactAt: needsNext ? toIso(nextContactAt) : null,
                    expectedOrderDate: expectedOrderDate || null,
                    expectedRevenue: expectedRevenue.trim() === '' ? null : expectedRevenue.trim(),
                    requestedProductIds: [],
                    requestedCategoryIds: [],
                    newContactId: newContactId || null,
                    noNeedReason: outcome === 'NO_CURRENT_NEED' ? reason.trim() : null,
                    lossReason: outcome === 'CUSTOMER_LOST' ? reason.trim() : null,
                    closedUntil: closedUntil || null,
                    followUp: needsNext ? { activityType: 'CALLBACK' } : null,
                    createQuote: outcome === 'QUOTE_REQUESTED' && createQuote ? {} : null,
                    createComplaint:
                      outcome === 'COMPLAINT_OR_PROBLEM'
                        ? { subject: complaintSubject.trim(), severity }
                        : null,
                  },
                },
                {
                  onSuccess: () => {
                    toast.success(t('salesAssistant.cockpit.outcomeDialog.success'));
                    onSuccess?.();
                    onOpenChange(false);
                  },
                  onError: (error) => toast.error(error.message),
                },
              );
            }}
          >
            {record.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {t('salesAssistant.cockpit.outcomeDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
